import { useState } from "react";
import { CalendarCheck } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { ClearancePass } from "./ClearancePass";

const STEPS = [
  ["01", "Book", "Pick a date and city. We line up your full-panel test."],
  ["02", "Test", "Quick, discreet collection. Results land on your phone."],
  ["03", "Clear", "Your 14-day clearance pass goes live — show it to sets and partners."],
  ["04", "Get paid back", "Claim your rebate, then do it all again in two weeks."],
];

const daysLeft = (iso) => {
  if (!iso) return 14;
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return Math.max(0, Math.min(14, 14 - diff));
};

export const Cycle = ({ onSchedule }) => {
  const [last, setLast] = useState("");
  const left = daysLeft(last);
  return (
    <section id="cycle" className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-36">
      <div className="grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-6">
          <Reveal><Eyebrow>The 14-day rhythm</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1]">
              Two weeks.<br /><span className="text-duo">One habit.</span>
            </h2>
          </Reveal>
          <ol className="mt-12 space-y-0">
            {STEPS.map(([n, t, d], i) => (
              <Reveal key={n} delay={0.08 * i}>
                <li className="group flex gap-6 py-6 border-t border-white/10 last:border-b">
                  <span className="font-mono text-sm text-[#5B95D6] pt-1.5">{n}</span>
                  <div>
                    <p className="font-display text-2xl font-bold group-hover:translate-x-2 transition-transform duration-500">{t}</p>
                    <p className="text-slate-400 mt-1">{d}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>

        <Reveal className="lg:col-span-6 lg:pl-10" delay={0.15}>
          <div className="relative rounded-[32px] bg-[#0E1326] border border-white/10 p-6 sm:p-10 overflow-hidden">
            <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#125AA0]/25 blur-3xl" />
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Clearance calculator</p>
            <label htmlFor="last-test" className="block mt-6 text-sm text-slate-300">When was your last test?</label>
            <input id="last-test" type="date" data-testid="cycle-calculator-input" value={last}
              max={new Date().toISOString().slice(0, 10)} onChange={(e) => setLast(e.target.value)}
              className="mt-2 w-full rounded-2xl bg-white/5 border border-white/10 px-4 py-3.5 text-white [color-scheme:dark] focus:outline-none focus:border-[#FF2A8D]" />
            <p data-testid="cycle-days-remaining" className="mt-8 font-display font-extrabold text-6xl sm:text-7xl leading-none">
              {left}<span className="text-2xl text-slate-400 font-bold ml-3">days left</span>
            </p>
            <p className="mt-3 text-slate-400 text-sm">
              {left === 0 ? "Your clearance window has closed — time to book." : left <= 3 ? "Book now so there's no gap in your clearance." : "You're covered. We'll nudge you before the window closes."}
            </p>
            <div className="mt-8 max-w-[300px]"><ClearancePass days={left} /></div>
            <button data-testid="cycle-schedule-btn" onClick={onSchedule}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-[#070A14] px-6 py-3.5 font-semibold hover:bg-[#125AA0] hover:text-white transition-colors">
              <CalendarCheck size={18} /> Book my next test
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
